import { z } from 'zod';
import { isoDate, uuid } from './posting.js';
/**
 * Die Verträge der Lizenzierung — Task 1 `[LZ-grund]` des Plans
 * `docs/superpowers/plans/2026-09-29-lizenzierung-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-29-lizenzierung-design.md`, Abschnitte 2,
 * 6.1, 8 und 9.1.
 *
 * **Zwei Arten Schema in einer Datei, und sie gehorchen verschiedenen Regeln.**
 *
 * - Die **Nutzlasten** der zwei signierten Bausteine (Lizenzschlüssel,
 *   Bescheid) und der Testlizenz sind der Vertrag mit einem **anderen
 *   Repository**, dem Lizenzserver (`D-19`). Ihre Feldnamen sind
 *   `snake_case`, wörtlich wie im JSON, das dort signiert wird — ein
 *   umbenanntes Feld wäre eine Nutzlast, die keine Signatur je trägt. Sie
 *   sind **nicht** `.strict()`: Spec 2.1 Regel 4 verlangt, dass unbekannte
 *   zusätzliche Felder **verworfen und nicht abgewiesen** werden, damit ein
 *   neuerer Server ältere Installationen nicht bricht. Eine inkompatible
 *   Änderung hebt stattdessen `v`.
 * - Die **Anfrage** an den Lizenzserver ist dagegen `.strict()` (`E-1543`):
 *   Sie verlässt die Installation, und kein Feld darf hinausgehen, das nicht
 *   ausdrücklich hier steht — kein Feldwert aus Belegen, Buchungen, Kunden
 *   oder Benutzern.
 * - Die **Antworten der fünf Routen** unter `/api/v1/lizenz` (Spec 8) sind
 *   `camelCase` und `.strict()` wie jeder andere Vertrag dieses Pakets.
 *
 * **Zeitpunkte in den Nutzlasten sind ganze Unix-Sekunden** (Spec 2.1 Regel
 * 5), in den Routenantworten ISO-8601-Zeichenketten. Beträge kommen nicht
 * vor.
 *
 * **Was hier NICHT steht:** die Prüfung einer Signatur, die Auswahl eines
 * Schlüssels, die Rechnung des Zustands. Das ist der Prüfer
 * (`apps/api/src/lizenz/`, Task `LZ-pruefung`); dieser Vertrag sagt nur, was
 * eine gültige Nutzlast und eine gültige Antwort **ist**.
 */
// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------
/**
 * Ganze Unix-Sekunden im sicheren Ganzzahlbereich von JavaScript (Spec 2.1
 * Regel 5). `z.number().int()` allein liesse `2 ** 60` zu — eine Zahl, die
 * JSON trägt, die aber beim Lesen in `number` schon gerundet ist.
 */
const unixSekunden = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** 16 Zufallsbytes, base64url ohne Auffüllung: genau 22 Zeichen (Einmalzahl, Folgemarke). */
const zufall16 = z.string().regex(/^[A-Za-z0-9_-]{22}$/u, '16 Bytes base64url (22 Zeichen) erwartet');
/**
 * Ein JWS in kompakter Form: drei base64url-Teile, höchstens 4096 Zeichen —
 * dieselbe Form, die `lizenz_hinterlegung_schluessel_form` (Migration `0246`)
 * in der Datenbank hält.
 */
export const jwsKompaktSchema = z
    .string()
    .max(4096, 'Höchstens 4096 Zeichen')
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/u, 'Drei base64url-Teile erwartet');
/** Umfang einer Lizenz: Installationen und Mandanten (Spec 2.2, `D-8`, `D-27`). */
export const lizenzUmfangSchema = z.object({
    instanzen: z.number().int().positive(),
    mandanten: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Aufzählungen
// ---------------------------------------------------------------------------
/**
 * Die sechs Zustände des Automaten (Spec 6.1, `D-10`) — in der Reihenfolge,
 * in der der Automat ihre Bedingungen prüft. Dieselbe Liste hält
 * `lizenz_zustand_zustand_bekannt` (Migration `0246`) an der gespeicherten
 * Spalte.
 */
export const lizenzZustandSchema = z.enum([
    'ohne_lizenz',
    'deaktiviert',
    'ohne_freischaltung',
    'abgelaufen',
    'warnung',
    'aktiv',
]);
/** Die fünf Ergebnisse eines Bescheids (Spec 1, 3.3) — `lizenz_bescheid_ergebnis_bekannt`. */
export const bescheidErgebnisSchema = z.enum([
    'freigeschaltet',
    'deaktiviert',
    'gabelung',
    'umfang_erschoepft',
    'unbekannt',
]);
/**
 * Die Bauart (Spec 10.4, `D-39`): `auslieferung` für jedes Abbild, das an
 * einen Kunden oder nach `accounting-stg` geht, `entwicklung` für
 * Quelltextlauf, Tests, Merge Train, E2E-Stack und lokales Docker. Sie ist
 * der Inhalt einer Programmdatei und kein Schalter.
 */
export const lizenzBauartSchema = z.enum(['auslieferung', 'entwicklung']);
/**
 * Die Durchsetzungsstufe (Spec 10.2, `D-20`) — eine Programmfassung, kein
 * Schalter. Seit `LZ-scharf` (2026-10-13) liefert jede Antwort `scharf`; der
 * Wert `meldend` bleibt in der Aufzählung, weil die Betriebsauskunft des
 * Installers (`docs/installer/vertrag-v1/auskunft.schema.json`) ihn nennt und
 * ein älterer Programmstand ihn noch melden kann (`D-52`).
 */
export const lizenzStufeSchema = z.enum(['meldend', 'scharf']);
/**
 * Die drei `typ`-Werte des JWS-Profils (Spec 2.1 Regel 2): Lizenzschlüssel,
 * Bescheid und — nur in der Bauart `entwicklung` — Testlizenz. Keiner wird als
 * ein anderer angenommen (`E-1531`). Derselbe Name wie im Vertrag des
 * Lizenzservers (`packages/contracts/src/lizenz-protokoll.ts` dort).
 */
export const jwsTypSchema = z.enum(['fa-lizenz', 'fa-bescheid', 'fa-testlizenz']);
/**
 * Die Gründe, aus denen der Prüfer einen JWS ablehnt — **in der Reihenfolge,
 * in der er prüft**; der erste zutreffende Grund gilt. Sie sind Teil des
 * Protokolls: `docs/lizenz/protokoll-v1/testvektoren.json` führt genau diese
 * Liste unter `ablehnungsgruende`, und jeder Vektor erwartet einen von ihnen
 * (`E-1530`). Ein neuer Grund ist deshalb eine Änderung des Protokolls in
 * beiden Repositories, nicht eine Stilfrage des Prüfers.
 */
export const lizenzAblehnungsgrundSchema = z.enum([
    'form_ungueltig',
    'kopf_ungueltig',
    'typ_unerwartet',
    'kid_unbekannt',
    'kid_gesperrt',
    'signatur_ungueltig',
    'version_unbekannt',
    'nutzlast_ungueltig',
]);
/**
 * Die Gründe, aus denen die Installation einen **formal gültigen** Bescheid
 * trotzdem verwirft (Spec 2.3, 4.3, `D-35`, `E-1533`) — in der Reihenfolge der
 * Prüfung; `testvektoren.json` führt sie unter `bindungsgruende`.
 */
export const lizenzBindungsgrundSchema = z.enum([
    'bindung_lid',
    'bindung_installation',
    'bindung_einmalzahl',
    'bindung_anfrage_sha256',
]);
// ---------------------------------------------------------------------------
// Die Nutzlasten der signierten Bausteine (Vertrag mit dem Lizenzserver)
// ---------------------------------------------------------------------------
/**
 * Die Nutzlast eines Lizenzschlüssels (`typ` `fa-lizenz`, Spec 2.2). Kein
 * Ablaufdatum; `umfang` ist der Stand **bei Ausstellung** und dient der
 * Anzeige und dem Wiederanlauf (Spec 12.7) — massgeblich für die Zählung ist
 * der Umfang, den der Lizenzserver heute führt.
 */
export const lizenzSchluesselNutzlastSchema = z.object({
    v: z.literal(1),
    lid: uuid,
    /** Anzeigename, höchstens 200 Codepunkte. */
    kunde: z.string().min(1).regex(/^[\s\S]{1,200}$/u, 'Höchstens 200 Zeichen'),
    umfang: lizenzUmfangSchema,
    ausgestellt: unixSekunden,
});
/**
 * Die FinTS-Produkt-Registrierungsnummer im Bescheid (Spec 2.3, Nachtrag
 * 2026-10-13, `D-51`): genau 25 Ziffern oder lateinische Buchstaben —
 * dieselbe Regel wie im Lizenzserver (`@lizenz/contracts`) und in
 * `openapi.json` des Vertrags. Der Wert selbst steht in keinem Repository.
 */
export const FINTS_PRODUKT_ID_MUSTER = /^[0-9A-Za-z]{25}$/u;
export const fintsProduktIdSchema = z
    .string()
    .regex(FINTS_PRODUKT_ID_MUSTER, '25 Ziffern oder lateinische Buchstaben erwartet');
/**
 * Die Nutzlast eines Bescheids (`typ` `fa-bescheid`, Spec 2.3).
 *
 * `gueltig_bis`, `folge` und `umfang` tragen einen Wert **genau bei**
 * `freigeschaltet` und sonst `null` — geprüft in der Verfeinerung unten, weil
 * ein Bescheid, der das verwechselt, entweder eine Freischaltung ohne
 * Folgemarke wäre (die nächste Anfrage legte `null` vor und würde als
 * Erstanfrage gezählt) oder ein negativer Bescheid mit einer Gültigkeit, die
 * niemand anwenden darf.
 *
 * `anfrage_sha256` bindet den Bescheid an die exakt gesendeten Rumpfbytes der
 * Anfrage (`D-35`, `E-1533`); ob er zur eigenen Anfrage passt, prüft der
 * Klient, nicht dieses Schema.
 *
 * `fints_produkt_id` (Spec 2.3, Nachtrag 2026-10-13, `D-51`, Auftrag
 * `FS-produktid`) ist optional und steht, wenn überhaupt, nur in einer
 * Freischaltung: die FinTS-Produkt-Registrierungsnummer, die die Installation
 * nimmt, wenn `FINTS_PRODUCT_ID` nicht gesetzt ist. Sie lebt nur im
 * Arbeitsspeicher (`LizenzZustandService.fintsProduktId()`); ein Bescheid in
 * der Datenbank trägt sie base64url-kodiert — die benannte Grenze aus `D-51`.
 */
export const bescheidNutzlastSchema = z
    .object({
    v: z.literal(1),
    lid: uuid,
    installation: uuid,
    einmalzahl: zufall16,
    anfrage_sha256: z.string().regex(/^[0-9a-f]{64}$/u, 'SHA-256 als 64 Hexziffern erwartet'),
    ergebnis: bescheidErgebnisSchema,
    zeit: unixSekunden,
    gueltig_bis: unixSekunden.nullable(),
    folge: zufall16.nullable(),
    umfang: lizenzUmfangSchema.nullable(),
    fints_produkt_id: fintsProduktIdSchema.optional(),
})
    .superRefine((b, ctx) => {
    if (b.fints_produkt_id !== undefined && b.ergebnis !== 'freigeschaltet') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fints_produkt_id'],
            message: 'fints_produkt_id steht nur in einer Freischaltung',
        });
    }
    const freigeschaltet = b.ergebnis === 'freigeschaltet';
    for (const feld of ['gueltig_bis', 'folge', 'umfang']) {
        if (freigeschaltet !== (b[feld] !== null)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [feld],
                message: freigeschaltet
                    ? `${feld} ist bei einer Freischaltung Pflicht`
                    : `${feld} steht nur in einer Freischaltung`,
            });
        }
    }
    if (b.gueltig_bis !== null && b.gueltig_bis <= b.zeit) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['gueltig_bis'],
            message: 'gueltig_bis liegt nicht nach zeit',
        });
    }
});
/**
 * Die Nutzlast der Testlizenz (`typ` `fa-testlizenz`, Spec 10.4, `D-39`):
 * ohne Ablaufdatum und **ohne** Bindung an eine Installation oder eine
 * Einmalzahl — sie ersetzt in der Bauart `entwicklung` Lizenzschlüssel und
 * Freischaltung zugleich.
 *
 * **Annahme dieses Vertrags**, weil die Spec die Felder nicht aufzählt: Sie
 * trägt dieselben Felder wie ein Lizenzschlüssel, damit die Lizenzseite im
 * Entwicklungsbau dieselbe Anzeige hat. Der Task, der den Prüfer baut
 * (`LZ-pruefung`), darf sie verengen; ein Feld mehr bräche nichts, weil
 * unbekannte Felder verworfen werden.
 */
export const testlizenzNutzlastSchema = lizenzSchluesselNutzlastSchema;
// ---------------------------------------------------------------------------
// Die Anfrage an den Lizenzserver (Spec 9.1)
// ---------------------------------------------------------------------------
/**
 * `POST /v1/freischaltungen` — genau diese acht Felder, dazu `lizenzschluessel`
 * nur in der ersten Anfrage nach einem signierten `unbekannt` (`D-38`,
 * `D-47`, `E-1543`). `.strict()`: Kein neuntes Feld verlässt die Installation
 * unbemerkt.
 */
export const freischaltungsAnfrageSchema = z
    .object({
    protokoll: z.literal(1),
    lizenz: uuid,
    installation: uuid,
    /** `null` bei einer Erstanfrage (Spec 4.2). */
    folge: zufall16.nullable(),
    einmalzahl: zufall16,
    programmstand: z
        .object({
        version: z.string().min(1).max(100),
        gitSha: z.string().regex(/^[0-9a-f]{40}$/u, 'Commit als 40 Hexziffern erwartet'),
    })
        .strict(),
    /** Die heute gültigen Zeilen in `datev_mandant` — 0 oder 1 (`D-27`). */
    mandanten: z.number().int().nonnegative(),
    /** HMAC-SHA256, base64url, 43 Zeichen; `null` bei leerem `datev_mandant` (`D-27`). */
    mandant_fingerabdruck: z
        .string()
        .regex(/^[A-Za-z0-9_-]{43}$/u, 'HMAC-SHA256 base64url (43 Zeichen) erwartet')
        .nullable(),
    lizenzschluessel: jwsKompaktSchema.optional(),
})
    .strict();
/** `200 { "bescheid": "<JWS>" }` — die einzige Antwort mit Wirkung (Spec 9.1). */
export const freischaltungsAntwortSchema = z.object({ bescheid: jwsKompaktSchema }).strict();
// ---------------------------------------------------------------------------
// Die fünf Routen unter /api/v1/lizenz (Spec 8)
// ---------------------------------------------------------------------------
/** Rumpf von `PUT /api/v1/lizenz` — der Lizenzschlüssel zum Hinterlegen. */
export const lizenzHinterlegenSchema = z.object({ lizenzschluessel: jwsKompaktSchema }).strict();
/**
 * Worauf die geltende Freischaltung lautet (Spec 4.4, 6.1): auf die eigene
 * Installationskennung, auf die Vorgängerkennung (gilt bis zu ihrem Ablauf
 * weiter, `D-36`) oder auf keine von beiden (dann `ohne_freischaltung`).
 */
export const freischaltungKennungSchema = z.enum(['eigene', 'vorgaenger', 'fremd']);
/** Ein Eintrag im Bescheidverlauf der Lizenzseite — die letzten 20. */
export const lizenzBescheidEintragSchema = z
    .object({
    ergebnis: bescheidErgebnisSchema,
    /** `zeit` des Bescheids (Uhr des Lizenzservers), ISO-8601. */
    zeit: z.string(),
    /** Wann die Zeile hier entstand, ISO-8601. */
    angelegtAm: z.string(),
})
    .strict();
/**
 * Antwort von `GET /api/v1/lizenz` — und, gleich geformt, von `PUT`,
 * `POST …/erneuerung` und `POST …/neue-installation` (Spec 8). Nur mit
 * `lizenz:verwalten`.
 */
export const lizenzAuskunftSchema = z
    .object({
    zustand: lizenzZustandSchema,
    /** `null`, solange kein Schlüssel hinterlegt ist. */
    lizenz: z
        .object({
        lid: uuid,
        kunde: z.string(),
        /** Der Umfang aus dem Schlüssel — Stand bei Ausstellung. */
        umfangBeiAusstellung: lizenzUmfangSchema,
        /** Der Umfang aus der letzten Freischaltung — der heute geführte. */
        umfangAktuell: lizenzUmfangSchema.nullable(),
    })
        .strict()
        .nullable(),
    installation: uuid,
    vorgaengerInstallation: uuid.nullable(),
    freischaltung: z
        .object({
        zeit: z.string(),
        gueltigBis: z.string(),
        lautetAuf: freischaltungKennungSchema,
    })
        .strict()
        .nullable(),
    letzterVersuchAm: z.string().nullable(),
    letzterErfolgAm: z.string().nullable(),
    letzterFehler: z.string().nullable(),
    /** `|Systemuhr − zeit| > 5 Minuten` bei der letzten Erneuerung (Spec 5.4). */
    uhrAbweichend: z.boolean(),
    neustartsSeitFreischaltung: z.number().int().nonnegative(),
    stufe: lizenzStufeSchema,
    bauart: lizenzBauartSchema,
    /**
     * Gilt die Testlizenz des Entwicklungsbaus (Spec 10.4)? Nur ohne
     * hinterlegten Schlüssel und nur, wenn der Bau eine prüfbare trägt; dann
     * ist `zustand` `aktiv`, ohne Freischaltung und ohne Restlaufzeit. Die
     * Lizenzseite sagt dazu ehrlich, dass sie nicht für den Betrieb ist.
     */
    testlizenz: z.boolean(),
    bescheide: z.array(lizenzBescheidEintragSchema).max(20),
})
    .strict();
/**
 * Antwort von `GET /api/v1/lizenz/hinweis` — das Hinweisband für jeden
 * Angemeldeten, **ohne Kennungen** (Spec 8, `D-21`).
 *
 * `meldend`, `meldendBis` und `endetOhneLizenz` stammen aus der meldenden
 * Stufe (Spec 10.2); seit `LZ-scharf` (2026-10-13) sind sie fest `false`,
 * `null` und `false` und bleiben, damit ältere Oberflächen die Antwort weiter
 * lesen (`D-52`). `betriebsfehler`: die Zeitmarke ist nicht
 * schreibbar (Spec 5.2 Regel 3) — dann zeigt das Band **keinen** Lizenztext,
 * weil der Fehler kein Lizenzzustand ist.
 */
export const lizenzHinweisSchema = z
    .object({
    zustand: lizenzZustandSchema,
    nurLesen: z.boolean(),
    restTage: z.number().int().nullable(),
    meldend: z.boolean(),
    meldendBis: isoDate.nullable(),
    endetOhneLizenz: z.boolean(),
    bauart: lizenzBauartSchema,
    betriebsfehler: z.boolean(),
})
    .strict();
//# sourceMappingURL=lizenz.js.map